'use client'; // mark this as a Client Component

import { useState, useEffect } from 'react';
import { LazyMotion, m, domAnimation } from 'framer-motion';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { CONFIG } from 'src/global-config';
import { useLocale } from 'src/locales/localization-provider';
import { useColorScheme } from '@mui/material';

// Benefits keys for translation
const BENEFITS = [
  { titleKey: 'reliability', descriptionKey: 'reliabilityDesc', iconColor: 'primary' },
  {
    titleKey: 'operationalExcellence',
    descriptionKey: 'operationalExcellenceDesc',
    iconColor: 'success',
  },
  { titleKey: 'innovation', descriptionKey: 'innovationDesc', iconColor: 'secondary' },
  { titleKey: 'partnership', descriptionKey: 'partnershipDesc', iconColor: 'secondary' },
  { titleKey: 'safetyCompliance', descriptionKey: 'safetyComplianceDesc', iconColor: 'success' },
  {
    titleKey: 'customerCentricity',
    descriptionKey: 'customerCentricityDesc',
    iconColor: 'primary',
  },
];

/* Minimal, layout-safe motion variants (keeps positions/layout identical) */
const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.06 },
  },
};

const itemVariant = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const imageVariant = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: 'easeOut' } },
};

export function GuidingPrincipals({ sx, ...other }) {
  const { t } = useLocale();
  const { mode } = useColorScheme();
  const [mounted, setMounted] = useState(false);
  const lang = localStorage.getItem('i18nextLng') || 'ar';

  // Only render translations after client mount (keeps your SSR behavior)
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null; // avoid SSR mismatch (keeps your original guard)

  const renderTexts = () => (
    <>
      <Typography
        variant="h2"
        component="h2"
        sx={{ textAlign: 'center', fontSize: '2.5rem !important', mb: 5 }}
      >
        {t('ourGuidingPrinciples')}
      </Typography>
      <Typography
        sx={{
          mt: 3,
          mx: 'auto',
          opacity: 0.72,
          maxWidth: 480,
          textAlign: 'center',
          mb: { xs: 5, md: 10 },
        }}
      >
        {t('guidingPrinciplesDescription')}
      </Typography>
    </>
  );

  const renderContent = (items, reverse) => (
    // This column remains a column flex layout — now motion-capable but same sx
    <Box
      component={m.div}
      variants={containerVariants}
      sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 4, md: 10 } }}
    >
      {items.map((benefit, index) => (
        <BenefitItem
          key={benefit.titleKey}
          benefit={benefit}
          index={index}
          reverse={reverse}
          t={t}
        />
      ))}
    </Box>
  );

  const renderImage = () => (
    // Wrap image in a motion-capable Box to animate without touching the inner image Box layout
    <Box component={m.div} variants={imageVariant} sx={{ display: { xs: 'none', md: 'block' } }}>
      <Box
        component="img"
        alt={t('Domestic_cargo_shipping_company_in_Saudi_Arabia_for_product_distribution')}
        src={`${CONFIG.assetsDir}/assets/images/home/ourguiding/b2b-product-distribution-transport.png`}
        sx={{
          width: 360,
          height: 500,
          objectFit: 'cover',
          borderRadius: 2,
        }}
      />
    </Box>
  );

  return (
    <Box
      component="section"
      sx={[
        {
          py: { xs: 5, md: 10 },
          bgcolor: mode === 'light' ? 'white' : 'background.neutral',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container>
        <LazyMotion features={domAnimation}>
          {/* Title + subtitle — motion-capable but exact same layout */}
          <Box
            component={m.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.32 }}
            variants={itemVariant}
            style={{ textAlign: 'center' }}
          >
            {renderTexts()}
          </Box>

          {/* Grid container — still the same grid (unchanged sx), now motion-enabled */}
          <Box
            component={m.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={containerVariants}
            sx={{
              display: 'grid',
              alignItems: 'center',
              gap: { xs: 4, md: 8 },
              gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' },
            }}
          >
            {renderContent(BENEFITS.slice(0, 3), true)}
            {renderImage()}
            {renderContent(BENEFITS.slice(-3))}
          </Box>
        </LazyMotion>
      </Container>
    </Box>
  );
}

/* BenefitItem uses motion-capable Box as root — keeps exact same sx/flex alignment */
function BenefitItem({ benefit, reverse, index, sx, t, ...other }) {
  return (
    <Box
      component={m.div}
      variants={itemVariant}
      sx={[
        {
          gap: 1,
          display: 'flex',
          flexDirection: { xs: 'row', md: reverse ? 'row-reverse' : 'row' },
          ...(reverse && { textAlign: { md: 'right' } }),
          ...(index === 1 && {
            pl: { md: 6 },
            ...(reverse && { pl: { md: 0 }, pr: { md: 6 } }),
          }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {/* Icon — same Box style, now supports whileHover without changing layout */}
      <Box
        component={m.div}
        whileHover={{ scale: 1.18, rotate: 6 }}
        transition={{ type: 'spring', stiffness: 220 }}
        sx={(theme) => ({
          m: 1,
          width: 16,
          height: 16,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundImage: `linear-gradient(to bottom, ${theme.vars.palette.primary.light}, ${theme.vars.palette.primary.main})`,
          ...(benefit.iconColor === 'secondary' && {
            backgroundImage: `linear-gradient(to bottom, ${theme.vars.palette.secondary.light}, ${theme.vars.palette.secondary.main})`,
          }),
          ...(benefit.iconColor === 'success' && {
            backgroundImage: `linear-gradient(to bottom, ${theme.vars.palette.success.light}, ${theme.vars.palette.success.main})`,
          }),
        })}
      />

      {/* Text — unchanged markup and sx, but motion-capable for hover micro-scale */}
      <Box component={m.div} whileHover={{ scale: 1.02 }} transition={{ duration: 0.18 }}>
        <Typography variant="h5" component="h5" sx={{ mb: 1 }}>
          {t(benefit.titleKey)}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t(benefit.descriptionKey)}
        </Typography>
      </Box>
    </Box>
  );
}
